import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const UserSettings = () => {
  const [user, setUser] = useState<any>(null);
  const [userDoc, setUserDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [notifications, setNotifications] = useState({
    newTasks: true,
    approvals: true,
    systemUpdates: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'User Settings — Pixano';
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (currentUser) {
          setUser(currentUser);
          setDisplayName(currentUser.displayName || '');
          
          // Load user document from Firestore
          const userDocRef = doc(db, 'adminUsers', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserDoc(userData);
            setTimezone(userData.timezone || 'UTC');
            setNotifications({
              newTasks: userData.notifications?.newTasks ?? true,
              approvals: userData.notifications?.approvals ?? true,
              systemUpdates: userData.notifications?.systemUpdates ?? false
            });
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      const userDocRef = doc(db, 'adminUsers', user.uid);
      await updateDoc(userDocRef, {
        displayName,
        timezone,
        notifications,
        updatedAt: new Date()
      });
      
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading user settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-800">Not Logged In</h2>
          <p className="text-red-600 mt-2">Please log in to view your settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">User Settings</h1>
        <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Account Settings</h2>
        </div>
        
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                value={user.email || ''}
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User ID
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                value={user.uid}
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">Your unique user identifier</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Preferences
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="mr-2" 
                    checked={notifications.newTasks}
                    onChange={(e) => setNotifications(prev => ({ ...prev, newTasks: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">Email notifications for new tasks</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="mr-2" 
                    checked={notifications.approvals}
                    onChange={(e) => setNotifications(prev => ({ ...prev, approvals: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">Email notifications for approvals</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="mr-2" 
                    checked={notifications.systemUpdates}
                    onChange={(e) => setNotifications(prev => ({ ...prev, systemUpdates: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">Email notifications for system updates</span>
                </label>
              </div>
            </div>

            {userDoc && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Account Status</h3>
                <div className="text-sm text-blue-700">
                  <p><strong>Role:</strong> {userDoc.permissions?.admin?.userManagement ? 'Super Admin' : 'Admin'}</p>
                  <p><strong>Created:</strong> {userDoc.createdAt ? new Date(userDoc.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}</p>
                  <p><strong>Last Updated:</strong> {userDoc.updatedAt ? new Date(userDoc.updatedAt.seconds * 1000).toLocaleDateString() : 'Never'}</p>
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <button 
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                onClick={() => window.location.reload()}
                className="ml-3 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;

